import { useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  Announcements,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import {
  CSS as DndCSS,
} from '@dnd-kit/utilities';
import { GoalCard } from './GoalCard';
import { Goal } from '@/types/goals';
import { cn } from '@/utils/cn';

// Create accessibility announcements for screen readers during keyboard reordering
const getAnnouncements = (goals: Goal[]): Announcements => ({
  onDragStart({ active }) {
    const item = goals.find(g => g.id === active.id);
    return `Started reordering ${item?.title || 'goal'}. Use Up and Down arrow keys to change position.`;
  },
  onDragOver({ active, over }) {
    if (over) {
      const activeItem = goals.find(g => g.id === active.id);
      const overItem = goals.find(g => g.id === over.id);
      const overIndex = goals.findIndex(g => g.id === over.id);
      return `${activeItem?.title || 'Goal'} is now at position ${overIndex + 1}, above ${overItem?.title || 'goal'}.`;
    }
    return undefined;
  },
  onDragEnd({ active, over }) {
    const item = goals.find(g => g.id === active.id);
    if (over && active.id !== over.id) {
      const newIndex = goals.findIndex(g => g.id === over.id) + 1;
      return `Finished reordering. ${item?.title || 'Goal'} is now priority ${newIndex}.`;
    }
    return `Reordering completed. ${item?.title || 'Goal'} position unchanged.`;
  },
  onDragCancel({ active }) {
    const item = goals.find(g => g.id === active.id);
    return `Reordering cancelled. ${item?.title || 'Goal'} returned to original position.`;
  },
});

interface SortableGoalCardProps {
  goal: Goal;
  onEdit: (goal: Goal) => void;
  onDelete: (goal: Goal) => void;
  onClick: (goal: Goal) => void;
  showDragHandle: boolean;
}

function SortableGoalCard({ goal, onEdit, onDelete, onClick, showDragHandle }: SortableGoalCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: goal.id });

  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "relative group",
        isSorting && "ring-2 ring-primary ring-offset-2"
      )}
    >
      <GoalCard
        goal={goal}
        onEdit={onEdit}
        onDelete={onDelete}
        onClick={onClick}
        isDragging={isDragging}
        dragHandleProps={showDragHandle ? {
          ...attributes,
          ...listeners,
          'aria-pressed': isDragging,
        } : undefined}
        showPriority={showDragHandle}
        className={cn(
          isDragging && "opacity-50 transform rotate-1 shadow-lg"
        )}
      />
    </div>
  );
}

interface GoalsListProps {
  goals: Goal[];
  onEdit: (goal: Goal) => void;
  onDelete: (goal: Goal) => void;
  onClick: (goal: Goal) => void;
  onReorder: (goals: Goal[]) => void;
  showDragHandles?: boolean;
}

export function GoalsList({
  goals,
  onEdit,
  onDelete,
  onClick,
  onReorder,
  showDragHandles = false
}: GoalsListProps) {
  // Memoize announcements to prevent unnecessary re-renders
  const announcements = useMemo(() => getAnnouncements(goals), [goals]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    console.log('🔄 Goal Drag End Event:', { activeId: active.id, overId: over?.id });

    // Only allow reordering when drag handles are shown (single aspiration selected)
    if (!showDragHandles) {
      console.log('❌ Drag handles disabled - no reordering allowed');
      return;
    }

    if (over && active.id !== over.id) {
      const oldIndex = goals.findIndex(item => item.id === active.id);
      const newIndex = goals.findIndex(item => item.id === over.id);
      
      console.log('📊 Goal Drag Details:', {
        activeId: active.id,
        overId: over.id,
        oldIndex,
        newIndex,
        originalOrder: goals.map(g => ({ id: g.id, title: g.title, priority: g.priority }))
      });

      // Create visual reorder for immediate UI feedback
      const visuallyReordered = arrayMove(goals, oldIndex, newIndex);
      
      // Recalculate priorities to ensure they're sequential and unique within the aspiration
      // Since we're only allowing drag/drop within a single aspiration, all goals have the same aspirationId
      const finalReordered = visuallyReordered.map((goal, index) => ({
        ...goal,
        priority: index + 1, // Sequential priorities starting from 1
        updatedAt: new Date()
      }));
      
      const draggedItem = goals[oldIndex];
      console.log('🎯 Goal Priority Recalculation:', {
        draggedItem: draggedItem.title,
        oldPriority: draggedItem.priority,
        oldIndex,
        newIndex,
        aspirationScope: 'Single aspiration - sequential priorities'
      });
      
      console.log('🔀 Final Goal Order with Sequential Priorities:', 
        finalReordered.map(g => ({ id: g.id, title: g.title, priority: g.priority }))
      );

      onReorder(finalReordered);
    } else {
      console.log('❌ No goal reorder needed:', { 
        sameItem: active.id === over?.id, 
        noOver: !over 
      });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
      accessibility={{
        announcements,
        screenReaderInstructions: {
          draggable: 'Press Space to start reordering this goal. Use Up and Down arrow keys to move it. Press Space to confirm or Escape to cancel.',
        },
      }}
    >
      <SortableContext
        items={goals.map(g => g.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-4">
          {goals.map(goal => (
            <SortableGoalCard
              key={goal.id}
              goal={goal}
              onEdit={onEdit}
              onDelete={onDelete}
              onClick={onClick}
              showDragHandle={showDragHandles}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}